import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { DataTable } from '@/components/CustomTable'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Pencil, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import type { ColumnDef } from '@tanstack/react-table'
import AppDialog, { ModalActions } from '@/components/CustomModal'
import Loading from '@/components/Loading'
import { Input } from '@/components/ui/input'
import { formatCFA } from '@/lib/utils'
import { useDebounce } from '@/hooks/useDebounce'

import {
  useExpensesListQuery,
  useExpensesSummaryQuery,
  useCreateExpenseMutation,
  useUpdateExpenseMutation,
  useDeleteExpenseMutation,
} from '@/hooks/useExpenses'

// à adapter si tu as déjà ces hooks :
import { useCategorySelectQuery } from '@/hooks/useCategories'
//import { useVendorSelectQuery } from '@/hooks/useVendors' // supposé

import type { ExpenseView } from '@/types/backend'
import { useTableUrlState } from '@/hooks/useTableUrlState'

export default function ExpensesPage() {
  const [searchParams, setSearchParams] = useSearchParams()

  const initialCat = searchParams.get('categoryId') ?? undefined
  const initialVendor = searchParams.get('vendorId') ?? undefined

  const [categoryIdFilter, setCategoryIdFilter] = useState<string | undefined>(
    initialCat
  )
  const [vendorIdFilter, _setVendorIdFilter] = useState<string | undefined>(
    initialVendor
  )

  useEffect(() => {
    const next = new URLSearchParams(searchParams)
    if (categoryIdFilter) next.set('categoryId', categoryIdFilter)
    else next.delete('categoryId')
    if (vendorIdFilter) next.set('vendorId', vendorIdFilter)
    else next.delete('vendorId')
    setSearchParams(next, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryIdFilter, vendorIdFilter])

  // Période par défaut = année courante
  const y = new Date().getFullYear()
  const [from, setFrom] = useState(`${y}-01-01`)
  const [to, setTo] = useState(`${y + 1}-01-01`)

  const {
    state: { search, pageIndex, pageSize },
    setSearch,
    setPageIndex,
    setPageSize,
  } = useTableUrlState('expenses', { search: '', pageIndex: 0, pageSize: 20 })

  const debouncedSearch = useDebounce(search, 400)

  const tableProps = {
    searchValue: search,
    onSearchChange: setSearch,
    currentPageIndex: pageIndex,
    currentPageSize: pageSize,
    onPageChange: setPageIndex,
    onPageSizeChange: setPageSize,
    paginationServer: true as const,
    sortingServer: false as const,
  }

  const { data: cats } = useCategorySelectQuery(true)
  /*const { data: vendors } = useVendorSelectQuery?.(true) ?? {
    data: [] as any[],
  }*/

  const { data: summary } = useExpensesSummaryQuery(from, to)
  const {
    data: list,
    status,
    isFetching,
  } = useExpensesListQuery(
    {
      search: debouncedSearch || undefined,
      categoryId: categoryIdFilter,
      vendorId: vendorIdFilter,
      from,
      to,
      page: pageIndex + 1,
      pageSize,
    },
    true
  )

  const total = list?.total ?? 0
  const rows = list?.data ?? []
  const pageCount = useMemo(
    () => Math.max(1, Math.ceil(total / pageSize)),
    [total, pageSize]
  )

  // Modales
  const [open, setOpen] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null)
  const [editing, setEditing] = useState<ExpenseView | null>(null)
  const [form, setForm] = useState<{
    name: string
    amount: string
    incurredOn: string
    categoryId: string
    vendorId: string
    note: string
  }>({
    name: '',
    amount: '',
    incurredOn: from,
    categoryId: 'NONE',
    vendorId: 'NONE',
    note: '',
  })

  const createMut = useCreateExpenseMutation()
  const updateMut = useUpdateExpenseMutation()
  const deleteMut = useDeleteExpenseMutation()

  const columns = useMemo<ColumnDef<ExpenseView>[]>(
    () => [
      {
        accessorKey: 'incurredOn',
        header: 'Date',
        cell: ({ row }) => row.original.incurredOn?.slice(0, 10) ?? '—',
      },
      {
        accessorKey: 'name',
        header: 'Libellé',
        cell: ({ row }) => row.original.name,
      },
      {
        accessorKey: 'vendor',
        header: 'Fournisseur',
        cell: ({ row }) => row.original.vendor?.name ?? '—',
      },
      {
        accessorKey: 'category',
        header: 'Catégorie',
        cell: ({ row }) => row.original.category?.name ?? '—',
      },
      {
        accessorKey: 'amount',
        header: 'Montant',
        cell: ({ row }) => formatCFA(row.original.amount),
      },
      {
        accessorKey: 'note',
        header: 'Note',
        cell: ({ row }) => row.original.note ?? '',
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <div className='flex items-center gap-3'>
            <Pencil
              size={18}
              className='cursor-pointer text-green-600 mr-4'
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
                setEditing(row.original)
                setForm({
                  name: row.original.name ?? '',
                  amount: String(row.original.amount ?? ''),
                  incurredOn: row.original.incurredOn?.slice(0, 10) ?? from,
                  categoryId: row.original.category?.id ?? 'NONE',
                  vendorId: row.original.vendor?.id ?? 'NONE',
                  note: row.original.note ?? '',
                })
                setOpen(true)
              }}
            />
            <Trash2
              size={18}
              className='cursor-pointer text-red-600 ml-4'
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
                setPendingDeleteId(row.original.id)
                setConfirmOpen(true)
              }}
            />
          </div>
        ),
      },
    ],
    [from]
  )

  const openCreate = () => {
    setEditing(null)
    setForm({
      name: '',
      amount: '',
      incurredOn: from,
      categoryId: 'NONE',
      vendorId: 'NONE',
      note: '',
    })
    setOpen(true)
  }

  const submit = async () => {
    const name = form.name.trim()
    const amount = parseFloat((form.amount || '').replace(',', '.'))
    const incurredOn = form.incurredOn
    const categoryId = form.categoryId !== 'NONE' ? form.categoryId : undefined
    const vendorId = form.vendorId !== 'NONE' ? form.vendorId : undefined
    const note = form.note.trim() || undefined

    if (!name) return toast.error('Libellé requis')
    if (!isFinite(amount) || amount <= 0) return toast.error('Montant invalide')

    try {
      if (editing) {
        await updateMut.mutateAsync({
          id: editing.id,
          name,
          amount,
          incurredOn,
          categoryId,
          vendorId,
          note,
        })
        toast.success('Dépense modifiée')
      } else {
        await createMut.mutateAsync({
          name,
          amount,
          incurredOn,
          categoryId,
          vendorId,
          note,
        })
        toast.success('Dépense créée')
      }
      setOpen(false)
    } catch (e: any) {
      toast.error('Erreur', {
        description: e?.response?.data?.message ?? 'Action échouée',
      })
    }
  }

  const confirmDelete = async () => {
    if (!pendingDeleteId) return
    try {
      await deleteMut.mutateAsync(pendingDeleteId)
      toast.success('Dépense supprimée')
      setConfirmOpen(false)
      setPendingDeleteId(null)
    } catch (e: any) {
      toast.error('Suppression échouée', {
        description: e?.response?.data?.message,
      })
    }
  }

  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-between gap-3'>
        <h3 className='text-2xl font-semibold'>Dépenses</h3>
        <div className='flex items-center gap-2'>
          <Button onClick={openCreate}>Ajouter</Button>
        </div>
      </div>

      {/* Filtres */}
      <div className='flex flex-wrap items-center gap-3'>
        <input
          type='date'
          className='h-9 rounded-md border px-2 text-sm'
          value={from}
          onChange={(e) => setFrom(e.target.value)}
        />
        <input
          type='date'
          className='h-9 rounded-md border px-2 text-sm'
          value={to}
          onChange={(e) => setTo(e.target.value)}
        />

        <Select
          value={categoryIdFilter ?? 'ALL'}
          onValueChange={(v) =>
            setCategoryIdFilter(v === 'ALL' ? undefined : v)
          }
        >
          <SelectTrigger className='w-[220px]'>
            <SelectValue placeholder='Filtrer par catégorie' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='ALL'>Toutes les catégories</SelectItem>
            {(cats ?? []).map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/*<Select
          value={vendorIdFilter ?? 'ALL'}
          onValueChange={(v) => setVendorIdFilter(v === 'ALL' ? undefined : v)}
        >
          <SelectTrigger className='w-[220px]'>
            <SelectValue placeholder='Filtrer par fournisseur' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='ALL'>Tous les fournisseurs</SelectItem>
            {(vendors ?? []).map((v: any) => (
              <SelectItem key={v.id} value={v.id}>
                {v.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>*/}
      </div>

      {/* Résumé rapide */}
      <div className='text-sm text-muted-foreground'>
        Période {from} → {to} — Total: <b>{summary?.totals.count ?? 0}</b> —
        Montant: <b>{formatCFA(summary?.totals.amount ?? 0)}</b>
      </div>

      {status === 'pending' ? (
        <Loading />
      ) : (
        <>
          <div className='text-sm text-muted-foreground'>
            Total lignes: <b>{total}</b>
          </div>
          <DataTable<ExpenseView, unknown>
            columns={columns}
            data={rows}
            {...tableProps}
            total={total}
            pageCount={pageCount}
            searchValue={search}
            onSearchChange={setSearch}
            rowClickable={false}
          />
          <div className='text-xs text-muted-foreground'>
            {isFetching ? 'Actualisation…' : null}
          </div>
        </>
      )}

      {/* Modale CRÉER/ÉDITER */}
      <AppDialog
        open={open}
        onOpenChange={setOpen}
        title={editing ? 'Modifier une dépense' : 'Ajouter une dépense'}
        description='Renseignez les champs requis.'
        asForm
        onSubmit={(e) => {
          e.preventDefault()
          submit()
        }}
        footer={
          <ModalActions
            onConfirm={submit}
            confirmLabel='Valider'
            confirmLoading={createMut.isPending || updateMut.isPending}
          />
        }
      >
        <div className='w-full px-4 py-2 space-y-3'>
          <div className='grid gap-1.5'>
            <Label>Libellé</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder='Ex: Carburant'
            />
          </div>

          <div className='grid gap-1.5'>
            <Label>Montant (CFA)</Label>
            <Input
              type='number'
              inputMode='decimal'
              step='1'
              min={0}
              value={form.amount}
              onChange={(e) =>
                setForm((f) => ({ ...f, amount: e.target.value }))
              }
              placeholder='Ex: 50 000'
            />
          </div>

          <div className='grid gap-1.5'>
            <Label>Date</Label>
            <Input
              type='date'
              value={form.incurredOn}
              onChange={(e) =>
                setForm((f) => ({ ...f, incurredOn: e.target.value }))
              }
            />
          </div>

          <div className='grid gap-1.5'>
            <Label>Catégorie</Label>
            <Select
              value={form.categoryId || 'NONE'}
              onValueChange={(v) =>
                setForm((f) => ({ ...f, categoryId: v === 'NONE' ? '' : v }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder='Choisir…' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='NONE'>— Aucune —</SelectItem>
                {(cats ?? []).map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/*<div className='grid gap-1.5'>
            <Label>Fournisseur</Label>
            <Select
              value={form.vendorId || 'NONE'}
              onValueChange={(v) =>
                setForm((f) => ({ ...f, vendorId: v === 'NONE' ? '' : v }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder='Choisir…' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='NONE'>— Aucun —</SelectItem>
                {(vendors ?? []).map((v: any) => (
                  <SelectItem key={v.id} value={v.id}>
                    {v.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>*/}

          <div className='grid gap-1.5'>
            <Label>Note (optionnel)</Label>
            <Input
              value={form.note}
              onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
              placeholder='...'
            />
          </div>
        </div>
      </AppDialog>

      {/* Modale SUPPRIMER */}
      <AppDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title='Supprimer la dépense'
        description='Cette action est irréversible.'
        footer={
          <ModalActions
            confirmVariant='destructive'
            confirmLabel='Supprimer'
            confirmLoading={deleteMut.isPending}
            onConfirm={confirmDelete}
          />
        }
      >
        <div className='px-4 py-2 text-sm'>
          Voulez-vous vraiment supprimer cette dépense ?
        </div>
      </AppDialog>
    </div>
  )
}
